'use client'

import Link from 'next/link'
import Image from 'next/image'
import {
  Mail,
  MapPin,
  ArrowRight
} from 'lucide-react'
import { useBranding } from '../../lib/contexts/BrandingContext'

export function Footer() {
  const { branding } = useBranding()

  const footerLinks = {
    solutions: [
      { name: 'Adapt Engine Platform', href: '/approach' },
      { name: 'Services', href: '/services' },
      { name: 'Unlock Your Potential', href: '/unlock' },
      { name: 'AI Maturity Assessment', href: '/?chat=true' },
      { name: 'Workshops & Training', href: '/workshops' }
    ],
    customers: [
      { name: 'Events & Webinars', href: '/events' },
      { name: 'Blog', href: '/blog' }
    ],
    company: [
      { name: 'Our Purpose', href: '/purpose' },
      { name: 'Trust & Security', href: '/trust' },
      { name: 'Login', href: '/login' }
    ],
    resources: [
      { name: 'Start Free Assessment', href: '/?chat=true' },
      { name: 'Client Portal', href: '/dashboard' },
      { name: 'Book a Strategy Call', href: 'https://calendly.com/alex-behmn/discovery-call' },
      { name: 'Contact Us', href: 'mailto:hello@behmn.com' }
    ]
  }

  return (
    <footer className="relative bg-gray-900/50 backdrop-blur-sm border-t border-gray-800">
      {/* Main Footer */}
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 py-12">

        {/* ── Mobile Layout (< md) ── */}
        <div className="md:hidden">
          {/* Logo + tagline centered */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <Image
                src="/hmn_logo.png"
                alt="hmn"
                width={100}
                height={30}
                className="h-8 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm font-diatype leading-relaxed max-w-xs mx-auto">
              The glue that binds AI capabilities with human adaptability.
            </p>
          </div>

          {/* CTA button */}
          <div className="flex justify-center mb-10">
            <Link
              href="/?chat=true"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold font-diatype text-sm hover:from-blue-500 hover:to-cyan-500 transition-all shadow-lg shadow-cyan-500/20"
            >
              Start Free Assessment
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* 2-column link grid */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-8 mb-10">
            <div>
              <h4 className="text-white font-semibold text-xs uppercase tracking-wider mb-3 font-gendy">Solutions</h4>
              <ul className="space-y-2.5">
                {footerLinks.solutions.map((link) => (
                  <li key={link.name}>
                    <Link href={link.href} className="text-gray-400 hover:text-cyan-400 transition-colors font-diatype text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs uppercase tracking-wider mb-3 font-gendy">Customers</h4>
              <ul className="space-y-2.5">
                {footerLinks.customers.map((link) => (
                  <li key={link.name}>
                    <Link href={link.href} className="text-gray-400 hover:text-cyan-400 transition-colors font-diatype text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs uppercase tracking-wider mb-3 font-gendy">Company</h4>
              <ul className="space-y-2.5">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <Link href={link.href} className="text-gray-400 hover:text-cyan-400 transition-colors font-diatype text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs uppercase tracking-wider mb-3 font-gendy">Resources</h4>
              <ul className="space-y-2.5">
                {footerLinks.resources.map((link) => (
                  <li key={link.name}>
                    <Link href={link.href} className="text-gray-400 hover:text-cyan-400 transition-colors font-diatype text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact row */}
          <div className="flex flex-col items-center gap-3 py-6 border-t border-gray-800/60 mb-6">
            <a href="mailto:hello@behmn.com" className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors font-diatype text-sm">
              <Mail className="w-4 h-4" />
              <span>hello@behmn.com</span>
            </a>
            <div className="flex items-center gap-2 text-gray-500 font-diatype text-sm">
              <MapPin className="w-4 h-4" />
              <span>Miami, FL</span>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="text-center space-y-3 pt-6 border-t border-gray-800/60">
            <div className="flex justify-center gap-6">
              <Link href="/privacy" className="text-gray-500 hover:text-cyan-400 text-xs transition-colors font-diatype">
                Privacy Policy
              </Link>
              <Link href="/terms" className="text-gray-500 hover:text-cyan-400 text-xs transition-colors font-diatype">
                Terms of Service
              </Link>
            </div>
            <div className="text-gray-600 text-xs font-diatype">
              © 2026 behmn. All rights reserved.
            </div>
          </div>
        </div>

        {/* ── Desktop Layout (md+) ── */}
        <div className="hidden md:block">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="mb-4">
                <Image
                  src="/hmn_logo.png"
                  alt="hmn"
                  width={100}
                  height={30}
                  className="h-8 w-auto"
                />
              </div>
              <p className="text-gray-300 mb-6 max-w-sm font-diatype leading-relaxed">
                The glue that binds AI capabilities with human adaptability. We help organizations embed behavioral change at every level to thrive in continuous transformation.
              </p>
              <div className="space-y-3">
                <a href="mailto:hello@behmn.com" className="flex items-center gap-3 text-gray-300 hover:text-cyan-400 transition-colors font-diatype">
                  <Mail className="w-5 h-5" />
                  <span>hello@behmn.com</span>
                </a>
                <div className="flex items-center gap-3 text-gray-300 font-diatype">
                  <MapPin className="w-5 h-5" />
                  <span>Miami, FL</span>
                </div>
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-white font-semibold mb-4 font-gendy">Solutions</h4>
              <ul className="space-y-2">
                {footerLinks.solutions.map((link) => (
                  <li key={link.name}>
                    <Link href={link.href} className="text-gray-300 hover:text-cyan-400 transition-colors font-diatype text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4 font-gendy">Customers</h4>
              <ul className="space-y-2">
                {footerLinks.customers.map((link) => (
                  <li key={link.name}>
                    <Link href={link.href} className="text-gray-300 hover:text-cyan-400 transition-colors font-diatype text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4 font-gendy">Company</h4>
              <ul className="space-y-2">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <Link href={link.href} className="text-gray-300 hover:text-cyan-400 transition-colors font-diatype text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4 font-gendy">Resources</h4>
              <ul className="space-y-2">
                {footerLinks.resources.map((link) => (
                  <li key={link.name}>
                    <Link href={link.href} className="text-gray-300 hover:text-cyan-400 transition-colors font-diatype text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex flex-row justify-between items-center gap-4">
              <div className="text-gray-400 text-sm font-diatype">
                © 2026 behmn. All rights reserved.
              </div>
              <div className="flex items-center gap-6">
                <Link href="/privacy" className="text-gray-400 hover:text-cyan-400 text-sm transition-colors font-diatype">
                  Privacy Policy
                </Link>
                <Link href="/terms" className="text-gray-400 hover:text-cyan-400 text-sm transition-colors font-diatype">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
